/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useReducer } from "react";
import { useOutletContext, useNavigate, useSearchParams } from "react-router-dom";
import { useGetSkillsSWR, useGetCompetitionsSWR, useGetRegionsSWR, useGetUsers, useChangePassword, useChangePicture, useDeletePicture } from "@/services/whoIsWhoService";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { toInitials } from "@/utils/toInitials";
import { roleToRoleDescription } from "@/utils/roleToRoleDescription";
import { Filter, Search, CircleUser, ChevronLeft, ChevronRight, Camera, Lock } from "lucide-react";
import { BarLoader } from "react-spinners";
import { apiURL } from "@/services/apiService";
import { useAuth } from "@/services/userService";

import SearchableSelector from "@/components/ui/searchableSelector";
import { updateSearchParams } from "@/utils/updateSearchParams";

/* DayJs */
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import localizedFormat from "dayjs/plugin/localizedFormat"
import "dayjs/locale/fr"
import utc from "dayjs/plugin/utc"


dayjs.locale("fr")
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)
dayjs.extend(utc)

const initialState = {
    filters: {
        skill_id: "",
        competition_id: "",
        region_id: "",
        role_type: "",
        skip: "0",
        limit: "10",
        order_field: "last_name",
        order_direction: "asc",
        name: "",
    },
    skills: [],
    competitions: [],
    regions: [],
    users: null,
    error_message: "",
    isLoading: false,
    page_size: 10,
    page: 1,
    total: 0
}



const ACTIONS = {
    SET_FILTER_FIELD: 'setFilterField',
    SET_ERROR_MESSAGE: 'setErrorMessage',
    SET_IS_LOADING: 'setIsLoading',
    SET_SKILLS: 'setSkills',
    SET_COMPETITIONS: 'setCompetitions',
    SET_REGIONS: 'setRegions',
    SET_USERS: 'setUsers',
    SET_TOTAL: 'setTotal',
    SET_PAGE: 'setPage',
    SET_PAGE_SIZE: 'setPageSize',
    SET_ORDER_FIELD: 'setOrderField',
}



const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_FILTER_FIELD:
            return { ...state, filters: { ...state.filters, [action.subtype]: action.payload } }
        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, error_message: action.payload }
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload }
        case ACTIONS.SET_SKILLS:
            return { ...state, skills: action.payload }
        case ACTIONS.SET_COMPETITIONS:
            return { ...state, competitions: action.payload }
        case ACTIONS.SET_REGIONS:
            return { ...state, regions: action.payload }
        case ACTIONS.SET_USERS:
            return { ...state, users: action.payload }
        case ACTIONS.SET_TOTAL:
            return { ...state, total: action.payload }
        case ACTIONS.SET_PAGE:
            return { ...state, page: action.payload }
        case ACTIONS.SET_PAGE_SIZE:
            return { ...state, page_size: action.payload }
        case ACTIONS.SET_ORDER_FIELD:
            console.log(action.payload)
            return { ...state, filters: { ...state.filters, order_field: action.payload.split(' ')[0], order_direction: action.payload.split(' ')[1] } }
        default:
            throw new Error("Unknown action.type " + action.type);
    }
}



export default function Index() {

    const [ , setPageTitle] = useOutletContext()

    const [ state, dispatch ] = useReducer(reducer, initialState)
    const [searchParams, setSearchParams] = useSearchParams();

    const navigate = useNavigate()

    const { auth } = useAuth()

    const {data: skills, isLoading: isLoadingSkills, error: errorSkills} = useGetSkillsSWR(state?.filters?.competition_id)
    const {data: competitions, isLoading: isLoadingCompetitions, error: errorCompetitions} = useGetCompetitionsSWR()
    const {data: regions, isLoading: isLoadingRegions, error: errorRegions} = useGetRegionsSWR()
    const getUsers = useGetUsers()
    const changePassword = useChangePassword()
    const changePicture = useChangePicture()
    const deletePicture = useDeletePicture()


    /* Callback de rafraichissement des utilisateurs */
    const refreshUsers = useCallback(async () => {
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });

        try {

            const filters = {
                skill_id: searchParams.get("skill_id") || null,
                competition_id: searchParams.get("competition_id") || null,
                region_id: searchParams.get("region_id") || null,
                role_type: searchParams.get("role_type") || null,
                skip: searchParams.get("skip") || "0",
                limit: searchParams.get("limit") || "10",
                order_field: searchParams.get("order_field") || "last_name",
                order_direction: searchParams.get("order_direction") || "asc",
                name: searchParams.get("name") || null,
            }
            const [users, total] = await getUsers(filters)
            dispatch({ type: ACTIONS.SET_USERS, payload: users })
            dispatch({ type: ACTIONS.SET_TOTAL, payload: total })
        } catch (error) {
            console.error('whoiswho::refreshUsers::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }


    }, [getUsers, searchParams])



    const hasModerationRight = useCallback((user) => {

        let result = false
        auth?.user?.roles?.map((role) => {
            if (role.rights.includes("whoiswho_admin")) {
                result = true
            } else if (role.rights.includes("whoiswho_skill_manage") && user.roles.filter((r) => r.skill_id === role.skill_id).length > 0) {
                result = true
            } else if (role.rights.includes("whoiswho_region_manage") && role.region !== null && user.roles.filter((r) => r.region?.id === role.region.id).length > 0) {
                result = true
            }
        })

        return result

    }, [auth])


    /* On met à jour le titre */
    useEffect(() => {
        setPageTitle("Who is Who")
    }, [setPageTitle])



    /* On gère un état commun de chargement */
    useEffect(() => {
        if (isLoadingSkills || isLoadingCompetitions || isLoadingRegions) {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        } else {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }, [isLoadingSkills, isLoadingCompetitions, isLoadingRegions])



    /* On gère un état commun d'erreur */
    useEffect(() => {
        if (errorSkills || errorCompetitions || errorRegions) {
            const error = errorSkills ? errorSkills : errorCompetitions ? errorCompetitions : errorRegions
            if (error?.response?.status === 401) {
                navigate("/login", {state: {from: "/whoiswho"}})
            } else if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        }
    }, [errorSkills, errorCompetitions, errorRegions, navigate])



    /* Lorsque l'on modifie la taille de la page on rafraichit */
    useEffect(() => {
        if(searchParams.get("page_size"))
            setSearchParams(updateSearchParams(searchParams, {
                page: 1,
                skip: String(((searchParams.get("page") || 1) - 1 )* (searchParams.get("page_size") || 10)),
                limit: String(searchParams.get("page_size") || 10)
            }))
            
    }, [searchParams.get("page_size")])



    /* Lorsque l'on modifie le champ de tri on rafraichit */
    useEffect(() => {
        if(state.total > 0)
            refreshUsers()
    }, [searchParams.get("order_field"), searchParams.get("order_direction")])



    /* Lorsque l'on modifie la page on rafraichit */
    useEffect(() => {
        if(searchParams.get("page"))
            setSearchParams(updateSearchParams(searchParams, {
                skip: String(( (searchParams.get("page") || 1) - 1) * (searchParams.get("page_size") || 10)),
                limit: String(searchParams.get("page_size") || 10)
            }))
    }, [searchParams.get("page")])



    /* Lorsque les états skip et limit ont changé, on rafraichit */
    useEffect(() => {
        if (state.total > 0)
            refreshUsers()
    }, [searchParams.get("skip"), searchParams.get("limit")])



    /* Gestion du bouton de soumission */
    const handleSubmit = async (e) => {
        e.preventDefault()
        setSearchParams(updateSearchParams(searchParams, { page: 1 }))
        refreshUsers()
    }



    /* Gestion du changement de mot de passe */
    const handleChangePassword = async (id) => {
        
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        try {
            const password = await changePassword(id)
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });
            alert("Le mot de passe a été changé par : " + password['password'])
        } catch (error) {
            console.error('whoiswho/index::handleChangePassword::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }



    /* Gestion du changement de photo */
    const handleChangePicture = async (id, file) => {
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        try {
            await changePicture(id, file)
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });
            await refreshUsers()
        } catch (error) {
            console.error('whoiswho/index::handleChangePicture::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }
        


    /* Gestion de la suppression de la photo */
    const handleDeletePicture = async (id) => {   
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        try {
            await deletePicture(id)
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });
            await refreshUsers()
        } catch (error) {
            console.error('whoiswho/index::handleDeletePicture::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }





    return (
        <div className="w-full flex flex-col gap-5">

            <div className="flex flex-row items-center gap-2 text-secondary">
                <Filter size={24} />
                <span className="text-secondary text-lg">Filtres</span>
            </div>

            {/* Filtres */}
            <div className="w-full flex flex-row gap-5 content-stretch flex-wrap">
                <div className="flex flex-col flex-grow gap-2 flex-1 text-secondary">
                    <span>Nom ou prénom :</span>
                    <input className="border border-gray-300 rounded-sm py-1 text-sm text-secondary"
                        onChange={(e) => setSearchParams(updateSearchParams(searchParams, { name: e.target.value} ))}
                        value={searchParams.get("name") || ""}
                        onKeyDown={(e) => { if (e.key === "Enter") { handleSubmit(e) }}}
                    />
                </div>
                <div className="flex flex-col flex-grow gap-2 flex-1 text-secondary">
                    <span>Compétition :</span>
                    <select className="border border-gray-300 rounded-sm py-1 text-sm text-secondary"
                        onChange={(e) => setSearchParams(updateSearchParams(searchParams, { competition_id: e.target.value} ))}
                        value={searchParams.get("competition_id") || ""}
                    >
                        <option value="">-</option>
                        {competitions?.map((competition, i) => {
                            return (
                                <option key={i} value={competition.id}>
                                    {competition.code} - {competition.name}
                                </option>
                            )
                        })}
                    </select>
                </div>
                <div className="flex flex-col flex-grow gap-2 flex-1 text-secondary">
                    <span>Métier :</span>
                    <SearchableSelector
                        key={skills?.length || 'loading'}
                        items={skills?.sort((a,b) => a.name.localeCompare(b.name)).map(skill => {
                            return {
                                id: skill.id,
                                name: skill.code + " - " + skill.name
                            }
                        }) || []}
                        selectedItem={Number(searchParams.get("skill_id")) || ""}
                        placeholder="Rechercher un métier"
                        onChange={ selectedOption => setSearchParams(updateSearchParams(searchParams, { skill_id: selectedOption })) }
                        styles={{
                            container: "text-sm",
                            tag: "text-sm w-full",
                            input: "text-sm px-2 py-1",
                            dropdown: "text-sm",
                            item: "text-sm",
                        }}
                    />
                    
                </div>
                <div className="flex flex-col flex-grow gap-2 flex-1 text-secondary">
                    <span>Région :</span>
                    <SearchableSelector
                        key={regions?.length || 'loading'}
                        items={regions?.sort((a, b) => a.name.localeCompare(b.name)) || []}
                        selectedItem={Number(searchParams.get("region_id")) || ""}
                        placeholder="Rechercher une région"
                        onChange={ selectedOption => setSearchParams(updateSearchParams(searchParams, { region_id: selectedOption })) }
                        styles={{
                            container: "text-sm",
                            tag: "text-sm w-full",
                            input: "text-sm px-2 py-1",
                            dropdown: "text-sm",
                            item: "text-sm",
                        }}
                    />
                    
                </div>
                <div className="flex flex-col flex-grow gap-2 flex-1 text-secondary">
                    <span>Mission :</span>
                    <select className="border-1 border-gray-300 rounded-sm py-1 text-sm text-secondary"
                        onChange={(e) => setSearchParams(updateSearchParams(searchParams, { role_type: e.target.value}))}
                        value={searchParams.get("role_type") || ""}
                    >
                        <option value="">-</option>
                        <option value="corr_region">Correspondant régional</option>
                        <option value="competiteur">Compétiteur</option>
                        <option value="expert_nat">Expert national</option>
                        <option value="expert_nat_adj">Expert national adjoint</option>
                        <option value="expert_int">Expert international</option>
                        <option value="expert_ref_ter">Expert référent territorial</option>
                        <option value="orga_metier_reg">Organisateur métier régional</option>
                        <option value="jure">Juré</option>
                        <option value="jure_supp">Juré suppléant</option>
                        <option value="team_leader">Team Leader</option>

                    </select>    
                </div>
            </div>

            {/* Boutons de recherche */}
            <div className="flex flex-row gap-2 items-center">
                <button className="flex gap-2 items-center border border-blue-500 rounded-md px-2 py-1 text-base hover:bg-blue-200"
                    onClick={(e) => handleSubmit(e)}
                    disabled={state.isLoading}
                >
                    <Search size={24} className="text-primary" />
                    Rechercher
                </button>
            </div>

            <hr />
            <div className="flex flex-row items-center gap-2 text-secondary">
                <CircleUser size={24} />
                <span className="text-primary text-lg">Résultats</span>
                {state.isLoading && <BarLoader color="#007bff" />}
                {state.error_message !== "" && <span className="text-red-500 text-base">{state.error_message}</span>}
            </div>




            {/* Liste des utilisateurs */}
            {state.users !== null ? 
                <>
                    <div className="w-full flex flex-row gap-5 justify-between">
                        <span className="text-secondary text-base">
                            {state.total} utilisateurs {state.total > 1 ? "trouvés" : "trouvé"}
                        </span>
                        <div className="flex flex-row gap-2 flex-wrap">
                            <div className="flex flex-row gap-2 items-center">
                                <span className="text-secondary text-base">Trier par :</span>
                                <select className="border-1 border-gray-400 py-1 text-sm text-secondary"
                                    onChange={(e) => setSearchParams(updateSearchParams(searchParams, { order_field: e.target.value.split(' ')[0], order_direction: e.target.value.split(' ')[1] }))} 
                                    value={searchParams.get("order_field") + " " + searchParams.get("order_direction")}
                                >
                                    <option value="last_name asc">Nom (A-Z)</option>
                                    <option value="last_name desc">Nom (Z-A)</option>
                                    <option value="first_name asc">Prénom (A-Z)</option>
                                    <option value="first_name desc">Prénom (Z-A)</option>
                                </select>                                
                            </div>
                            <div className="flex flex-row gap-2 items-center">
                                <select className="border-1 border-gray-400 py-1 text-sm text-secondary"
                                    onChange={(e) => setSearchParams(updateSearchParams(searchParams, { page_size: e.target.value }))}
                                    value={searchParams.get("page_size")}
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                                <span className="text-secondary text-base">résultats par page</span>
                            </div>
                        </div>
                    </div>


                    {/* Affichage d'un utilisateur */}
                    <div className="w-full flex flex-col gap-8">
                    {
                        state.users.map((user, i) => {
                            return (
                                <div key={i} className="flex flex-row items-center gap-10 text-secondary">
                                    <div className="flex flex-col">
                                        { 
                                            user?.picture_url
                                                ? <img src={apiURL + user.picture_url} alt="Photo de l'utilisateur" 
                                                    className={`rounded-full w-32 h-32 border-2 ${user.is_online && "border-green-500 border-2"}`} 
                                                    onDragOver={(e) => e.preventDefault()}
                                                    onDrop={async (e) => {
                                                        e.preventDefault();
                                                        if (e.dataTransfer.files.length > 0 && user.whoiswho_hide_picture === false) {
                                                            await handleChangePicture(user.id, e.dataTransfer.files[0]);
                                                        }
                                                    }}
                                                />
                                                : <div 
                                                    className={`rounded-full bg-red-300 w-32 h-32 text-2xl flex items-center justify-center ${user.is_online && "border-green-500 border-2"}`}
                                                    onDragOver={(e) => e.preventDefault()}
                                                    onDrop={async (e) => {
                                                        e.preventDefault();
                                                        if (e.dataTransfer.files.length > 0 && user.whoiswho_hide_picture === false) {
                                                            await handleChangePicture(user.id, e.dataTransfer.files[0]);
                                                        }
                                                    }}
                                                >
                                            
                                            {toInitials(user.first_name, user.last_name)}
                                        </div> }
                                    </div>
                                    <div className="flex flex-col flex-1">
                                        <span className="text-primary text-lg flex flex-row gap-5 items-center">
                                            {user.last_name}
                                            
                                        </span>
                                        <span className="text-secondary text-base">{user.first_name}</span>
                                        { user?.whoiswho_hide_email === false
                                            ? <a href={"mailto:" + user.email} className="text-secondary text-base">{user.email}</a>
                                            : <span className="text-secondary text-base italic">L&apos;email est masqué par l&apos;utilisateur</span>
                                        }
                                        
                                        <div className="flex flex-row gap-2 items-center flex-wrap">
                                        {
                                            user.roles.reverse().map( (role, i) => {
                                                return <span key={i} className="text-secondary text-sm border-gray-300 border rounded-md px-2">{roleToRoleDescription(role)}</span>
                                            }) 
                                        }
                                        </div>

                                    </div>

                                    {hasModerationRight(user) && <>
                                        <div className="flex flex-col place-items-end gap-1">
                                            {
                                                user.last_seen ? <span className="text-secondary text-sm flex flex-row gap-1">
                                                        Vu : 
                                                        <span title={dayjs.utc(user.last_seen).local().format('lll')}>{dayjs.utc(user.last_seen).local().fromNow()}</span>
                                                    </span>
                                                : <span className="text-secondary text-sm flex flex-row gap-1">Jamais connecté</span>
                                            }
                                            
                                            {
                                                user.last_seen === null &&
                                                    <button className="text-blue-500 hover:text-blue-800 underline text-sm flex flex-row gap-1 items-center"
                                                        onClick={async (e) => {e.preventDefault(), await handleChangePassword(user.id)}}
                                                    >
                                                        <Lock size={12} />Modifier le mot de passe
                                                    </button>
                                            }

                                            { user?.whoiswho_hide_picture === false ? <label className="text-blue-500 text-sm underline hover:text-blue-800 cursor-pointer flex flex-row items-center gap-1" htmlFor={`picture-${user.id}`}>
                                                <Camera size={12} />Modifier la photo de profil
                                                <input type="file" className="hidden" id={`picture-${user.id}`} onChange={async (e) => {
                                                    e.preventDefault();
                                                    if (e.target.files.length > 0) {
                                                        await handleChangePicture(user.id, e.target.files[0]);
                                                    }
                                                }}/>
                                            </label> : <span className="text-secondary text-sm italic">La photo de profil est masquée par l&apos;utilisateur</span> }

                                            { 
                                                user?.whoiswho_hide_picture === false && user.picture_url && <span className="text-red-500 text-sm underline hover:text-red-800 cursor-pointer" onClick={(e) => {
                                                    e.preventDefault();
                                                    handleDeletePicture(user.id)
                                                }}>
                                                    Supprimer la photo de profil
                                                </span> 
                                            }
                                        </div>
                                    </>}

                                </div>
                            )
                        })
                    }
                    </div>

                    <div className="w-full flex flex-row gap-5 justify-end">
                        
                        <div className="flex flex-row gap-2 items-center border-collapse">
                            
                            <button 
                                className="text-base border border-gray-400 px-2 rounded-s-xl hover:bg-gray-200 text-secondary border-collapse disabled:cursor-not-allowed"
                                disabled={searchParams.get("page") <= 1}
                                onClick={(e) => {e.preventDefault(); setSearchParams(updateSearchParams(searchParams, { page: Number(searchParams.get("page") || 1) - 1 }))}}
                            >
                                <ChevronLeft size={24} />
                            </button>

                            <span className="text-secondary text-base border border-gray-400 px-2 border-collapse">
                                {searchParams.get("page")} / {Math.ceil(state.total / Number(searchParams.get("page_size") || 10))}
                            </span>

                            <button 
                                className="text-base border border-gray-400 px-2 rounded-e-xl hover:bg-gray-200 text-secondary border-collapse disabled:cursor-not-allowed"
                                disabled={searchParams.get("page") >= Math.ceil(state.total / searchParams.get("page_size"))}
                                onClick={(e) => {e.preventDefault(); setSearchParams(updateSearchParams(searchParams, { page: Number(searchParams.get("page") || 1) + 1 }))}}
                            >
                                <ChevronRight size={24} />
                            </button>

                        </div>
                    </div>
                </>
            : <span className="text-secondary text-base">Lancer la recherche pour afficher les utilisateurs</span>
            }
        </div>
    )
    
}